import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox,
  useToast,
  Spinner,
  Flex,
  Text,
  IconButton,
  VStack,
  HStack,
  Box,
  Badge,
  Divider,
} from "@chakra-ui/react";
import {
  SearchIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CloseIcon,
} from "@chakra-ui/icons";
import { supabaseClients } from "../../lib/supabaseClient";

const PAGE_SIZE = 10;

const MusicImportModal = ({
  isOpen,
  onClose,
  dataType,
  onImportSuccess,
  searchFields,
  displayColumns,
}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItems, setSelectedItems] = useState({});
  const toast = useToast();
  const [error, setError] = useState(null);
  const [failedItems, setFailedItems] = useState([]);

  // dataType에 따른 검색 필드 정의
  const getSearchFields = () => {
    switch (dataType) {
      case "track":
        return [
          "id",
          "track_name_kr",
          "isrc",
          "track_code",
          "album",
          "musician",
        ];
      case "album":
        return [
          "id",
          "name_album_en",
          "name_album_kr",
          "code_album",
          "musician",
          "published_at",
          "user",
        ];
      case "musician":
        return ["id", "name_musician", "name_korean", "name_english", "user"];
      default:
        return searchFields || [];
    }
  };

  // dataType에 따른 표시 컬럼 정의
  const getDisplayColumns = () => {
    switch (dataType) {
      case "track":
        return [
          { key: "track_name_kr", label: "트랙명" },
          { key: "isrc", label: "ISRC" },
          { key: "track_code", label: "트랙코드" },
          { key: "album", label: "앨범명" },
          { key: "musician", label: "뮤지션" },
        ];
      case "album":
        return [
          { key: "name_album_en", label: "앨범명(EN)" },
          { key: "name_album_kr", label: "앨범명(KR)" },
          { key: "code_album", label: "앨범코드" },
          { key: "musician", label: "뮤지션" },
          { key: "published_at", label: "발매일" },
          { key: "user", label: "사용자" },
        ];
      case "musician":
        return [
          { key: "name_musician", label: "뮤지션명" },
          { key: "name_korean", label: "한국어 이름" },
          { key: "name_english", label: "영어 이름" },
          { key: "user", label: "사용자" },
        ];
      default:
        return displayColumns || [];
    }
  };

  // 모달이 열리거나 닫힐 때 상태 초기화
  useEffect(() => {
    if (isOpen) {
      setData([]);
      setLoading(false);
      setImporting(false);
      setPage(1);
      setTotalPages(1);
      setSearchTerm("");
      setSearchQuery("");
      setSelectedItems({});
      setError(null);
      setFailedItems([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !supabaseClients.plpl) {
      setError("외부 데이터베이스 연결에 실패했습니다. 관리자에게 문의하세요.");
    }
  }, [isOpen]);

  const fetchData = useCallback(async () => {
    if (!supabaseClients.plpl) {
      return;
    }
    setLoading(true);
    let query = supabaseClients.plpl
      .from(dataType)
      .select("*", { count: "exact" });

    if (searchQuery) {
      const searchFields = getSearchFields();
      const orConditions = searchFields
        .map((field) => `${field}.ilike.%${searchQuery}%`)
        .join(",");
      query = query.or(orConditions);
    }

    const {
      data: result,
      error,
      count,
    } = await query
      .order("created_date", { ascending: false })
      .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);

    if (error) {
      toast({
        title: "데이터 조회 실패",
        description: error.message,
        status: "error",
      });
      setData([]);
    } else {
      setData(result);
      setTotalPages(Math.ceil((count ?? 0) / PAGE_SIZE));
    }
    setLoading(false);
  }, [page, searchQuery, dataType, toast]);

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen, fetchData]);

  const handleSelect = (item) => {
    setSelectedItems((prev) => {
      const newSelected = { ...prev };
      if (newSelected[item.id]) {
        delete newSelected[item.id];
      } else {
        newSelected[item.id] = item;
      }
      return newSelected;
    });
  };

  const handleRemoveSelected = (itemId) => {
    setSelectedItems((prev) => {
      const newSelected = { ...prev };
      delete newSelected[itemId];
      return newSelected;
    });
  };

  const handleImport = async () => {
    setImporting(true);
    const itemsToImport = Object.values(selectedItems);
    const failedItemsList = [];

    if (itemsToImport.length === 0) {
      toast({ title: "항목을 선택해주세요.", status: "warning" });
      setImporting(false);
      return;
    }

    try {
      if (dataType === "album") {
        // Album 가져오기 시 track과 musician도 함께 가져오기
        for (const album of itemsToImport) {
          try {
            // 2. Album을 main DB에 저장
            const { data: savedAlbum, error: albumError } =
              await supabaseClients.main
                .from("album")
                .insert(album)
                .select()
                .single();

            if (albumError) {
              failedItemsList.push({
                type: "album",
                name:
                  album.name_album_kr || album.name_album_en || "앨범명 없음",
                error: `앨범 저장 실패: ${albumError.message}`,
                originalData: album,
              });
              continue;
            }

            // 3. 해당 앨범의 track들 조회
            const { data: tracks, error: tracksError } =
              await supabaseClients.plpl
                .from("track")
                .select("*")
                .eq("album", album.id);

            if (tracksError) {
              failedItemsList.push({
                type: "album",
                name:
                  album.name_album_kr || album.name_album_en || "앨범명 없음",
                error: `트랙 조회 실패: ${tracksError.message}`,
                originalData: album,
              });
              continue;
            }

            // 4. 각 track에 대해 musician 처리 후 저장
            for (const track of tracks) {
              try {
                // 4-1. Track의 musician을 plpl DB에서 조회하여 저장
                const { data: plplMusician, error: musicianError } =
                  await supabaseClients.plpl
                    .from("musician")
                    .select("*")
                    .eq("id", track.musician);

                console.log(plplMusician);

                if (plplMusician && !musicianError) {
                  const { error: saveMusicianError } =
                    await supabaseClients.main
                      .from("musician")
                      .insert(plplMusician);

                  if (saveMusicianError) {
                    failedItemsList.push({
                      type: "musician",
                      name: track.musician || "뮤지션명 없음",
                      error: `뮤지션 저장 실패: ${saveMusicianError.message}`,
                      originalData: plplMusician,
                    });
                  }
                }

                // 4-3. Track 데이터 정리 및 저장 (모든 컬럼값 유지)
                const { error: trackSaveError } = await supabaseClients.main
                  .from("track")
                  .insert(track);

                if (trackSaveError) {
                  failedItemsList.push({
                    type: "track",
                    name:
                      track.track_name_kr ||
                      track.track_name_en ||
                      "트랙명 없음",
                    error: `트랙 저장 실패: ${trackSaveError.message}`,
                    originalData: track,
                  });
                }
              } catch (trackError) {
                failedItemsList.push({
                  type: "track",
                  name:
                    track.track_name_kr || track.track_name_en || "트랙명 없음",
                  error: `트랙 처리 중 오류: ${trackError.message}`,
                  originalData: track,
                });
              }
            }
          } catch (albumError) {
            failedItemsList.push({
              type: "album",
              name: album.name_album_kr || album.name_album_en || "앨범명 없음",
              error: `앨범 처리 중 오류: ${albumError.message}`,
              originalData: album,
            });
          }
        }

        const successCount =
          itemsToImport.length -
          failedItemsList.filter((item) => item.type === "album").length;
        const message =
          failedItemsList.length > 0
            ? `${successCount}개 앨범 성공, ${failedItemsList.length}개 항목 실패`
            : `${itemsToImport.length}개 앨범과 관련 데이터를 가져왔습니다.`;

        toast({
          title:
            failedItemsList.length > 0 ? "일부 가져오기 완료" : "가져오기 완료",
          description: message,
          status: failedItemsList.length > 0 ? "warning" : "success",
        });

        setFailedItems(failedItemsList);
      } else {
        // 기존 로직 (track, musician)
        const cleanedItemsToImport = itemsToImport.map((item) => {
          const { id, created_date, ...rest } = item;
          return rest;
        });

        const { error } = await supabaseClients.main
          .from(dataType)
          .insert(cleanedItemsToImport);

        if (error) {
          throw error;
        }

        toast({
          title: `${cleanedItemsToImport.length}개 항목을 가져왔습니다.`,
          status: "success",
        });
      }

      onImportSuccess();
      onClose();
      setSelectedItems({});
    } catch (error) {
      console.error("가져오기 실패:", error);
      toast({
        title: "가져오기 실패",
        description: error.message,
        status: "error",
      });
    } finally {
      setImporting(false);
    }
  };

  const handleSearchTrigger = () => {
    setPage(1);
    setSearchQuery(searchTerm);
  };

  const renderCell = (item, column) => {
    const value = item[column.key];
    if (column.key === "cover_image_url" && value) {
      return (
        <img
          src={value}
          alt="cover"
          style={{ width: "50px", height: "50px", objectFit: "cover" }}
        />
      );
    }
    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }
    return value;
  };

  const renderSelectedItem = (item) => {
    switch (dataType) {
      case "track":
        return (
          <Box>
            <Text fontWeight="medium">
              {item.track_name_kr || "트랙명 없음"}
            </Text>
            <Text fontSize="sm" color="gray.600">
              {item.album || "앨범명 없음"} - {item.musician || "뮤지션 없음"}
            </Text>
            <Text fontSize="xs" color="gray.500">
              ISRC: {item.isrc || "없음"} | 코드: {item.track_code || "없음"}
            </Text>
          </Box>
        );
      case "album":
        return (
          <Box>
            <Text fontWeight="medium">
              {item.name_album_kr || item.name_album_en || "앨범명 없음"}
            </Text>
            <Text fontSize="sm" color="gray.600">
              {item.musician || "뮤지션 없음"}
            </Text>
            <Text fontSize="xs" color="gray.500">
              코드: {item.code_album || "없음"} | 사용자: {item.user || "없음"}
            </Text>
          </Box>
        );
      case "musician":
        return (
          <Box>
            <Text fontWeight="medium">
              {item.name_musician ||
                item.name_korean ||
                item.name_english ||
                "뮤지션명 없음"}
            </Text>
            <Text fontSize="sm" color="gray.600">
              {item.user || "사용자 없음"}
            </Text>
          </Box>
        );
      default:
        return <Text>{item.track_name_kr || item.name || "항목명 없음"}</Text>;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{dataType} 가져오기</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {error ? (
              <Text color="red.500">{error}</Text>
            ) : (
              <>
                {/* 실패한 항목 섹션 */}
                {failedItems.length > 0 && (
                  <Box>
                    <Text fontWeight="bold" mb={2} color="red.600">
                      실패한 항목 ({failedItems.length}개)
                    </Text>
                    <Box
                      border="1px"
                      borderColor="red.200"
                      borderRadius="md"
                      p={3}
                      maxH="200px"
                      overflowY="auto"
                      bg="red.50"
                    >
                      <VStack spacing={2} align="stretch">
                        {failedItems.map((item, index) => (
                          <Box
                            key={index}
                            p={2}
                            bg="white"
                            borderRadius="md"
                            border="1px"
                            borderColor="red.300"
                          >
                            <Flex justify="space-between" align="start">
                              <Box flex="1">
                                <Text fontWeight="medium" color="red.700">
                                  [{item.type.toUpperCase()}] {item.name}
                                </Text>
                                <Text fontSize="sm" color="red.600" mt={1}>
                                  {item.error}
                                </Text>
                              </Box>
                            </Flex>
                          </Box>
                        ))}
                      </VStack>
                    </Box>
                    <Divider my={4} />
                  </Box>
                )}

                {/* 선택된 항목 섹션 */}
                {Object.keys(selectedItems).length > 0 && (
                  <Box>
                    <Text fontWeight="bold" mb={2}>
                      선택된 항목 ({Object.keys(selectedItems).length}개)
                    </Text>
                    <Box
                      border="1px"
                      borderColor="gray.200"
                      borderRadius="md"
                      p={3}
                      maxH="200px"
                      overflowY="auto"
                    >
                      <VStack spacing={2} align="stretch">
                        {Object.values(selectedItems).map((item) => (
                          <Flex
                            key={item.id}
                            justify="space-between"
                            align="center"
                            p={2}
                            bg="gray.50"
                            borderRadius="md"
                          >
                            {renderSelectedItem(item)}
                            <IconButton
                              size="sm"
                              icon={<CloseIcon />}
                              onClick={() => handleRemoveSelected(item.id)}
                              colorScheme="red"
                              variant="ghost"
                            />
                          </Flex>
                        ))}
                      </VStack>
                    </Box>
                    <Divider my={4} />
                  </Box>
                )}

                <HStack>
                  <InputGroup maxW="300px">
                    <InputLeftElement
                      pointerEvents="none"
                      children={<SearchIcon color="gray.300" />}
                    />
                    <Input
                      placeholder="검색"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) =>
                        e.key === "Enter" && handleSearchTrigger()
                      }
                    />
                  </InputGroup>
                  <Button ml={2} onClick={handleSearchTrigger}>
                    검색
                  </Button>
                </HStack>
                {loading ? (
                  <Flex justify="center" p={10}>
                    <Spinner />
                  </Flex>
                ) : (
                  <>
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>
                            <Checkbox
                              isChecked={
                                data.length > 0 &&
                                Object.keys(selectedItems).length ===
                                  data.length
                              }
                              onChange={() => {
                                if (
                                  Object.keys(selectedItems).length ===
                                  data.length
                                ) {
                                  setSelectedItems({});
                                } else {
                                  const newSelected = {};
                                  data.forEach(
                                    (item) => (newSelected[item.id] = item)
                                  );
                                  setSelectedItems(newSelected);
                                }
                              }}
                            />
                          </Th>
                          {getDisplayColumns().map((col) => (
                            <Th key={col.key}>{col.label}</Th>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {data.map((item) => (
                          <Tr key={item.id}>
                            <Td>
                              <Checkbox
                                isChecked={!!selectedItems[item.id]}
                                onChange={() => handleSelect(item)}
                              />
                            </Td>
                            {getDisplayColumns().map((col) => (
                              <Td key={col.key}>{renderCell(item, col)}</Td>
                            ))}
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                    <Flex justify="center" align="center" mt={6}>
                      <IconButton
                        icon={<ChevronLeftIcon />}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        isDisabled={page === 1}
                        size="sm"
                      />
                      <Text mx={4} fontSize="sm">
                        Page {page} of {totalPages}
                      </Text>
                      <IconButton
                        icon={<ChevronRightIcon />}
                        onClick={() =>
                          setPage((p) => Math.min(totalPages, p + 1))
                        }
                        isDisabled={page >= totalPages}
                        size="sm"
                      />
                    </Flex>
                  </>
                )}
              </>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            colorScheme="blue"
            mr={3}
            onClick={handleImport}
            isLoading={importing}
            disabled={Object.keys(selectedItems).length === 0 || !!error}
          >
            가져오기 ({Object.keys(selectedItems).length}개)
          </Button>
          <Button variant="ghost" onClick={onClose}>
            취소
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MusicImportModal;
